import React, { useState } from 'react';
import PropTypes from 'prop-types';
import * as R from 'ramda';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { DrawerLayoutWrapper } from 'common';
import ProgressBar from 'feature/dispatching/components/ProgressBar';
import { useGetStatesQuery } from '../driverStatusEndpoints';

const notEmpty = R.compose(R.not, R.isEmpty);

const DriverInformation = ({ onClose, next, driverState, setDriverState }) => {
  const { firstName, lastName, driversLicense, driversLicenseState } = driverState;

  const { data: states = [], isLoading: isLoadingStates } = useGetStatesQuery();

  const [validationMessages, setValidationMessages] = useState({
    driversLicense: '',
    driversLicenseState: {},
    firstName: '',
    lastName: '',
  });

  const isInvalid = R.or(
    R.or(R.isEmpty(firstName), R.isEmpty(lastName)),
    R.or(R.isEmpty(driversLicense), R.isEmpty(driversLicenseState))
  );

  const validateFirstName = R.ifElse(
    R.isEmpty,
    R.compose(setValidationMessages, R.assoc('firstName'), R.always('First Name required')),
    R.compose(setValidationMessages, R.assoc('firstName'), R.always(''))
  );

  const validateLastName = R.ifElse(
    R.isEmpty,
    R.compose(setValidationMessages, R.assoc('lastName'), R.always('Last Name required')),
    R.compose(setValidationMessages, R.assoc('lastName'), R.always(''))
  );

  const validateDriversLicense = R.ifElse(
    R.isEmpty,
    R.compose(
      setValidationMessages,
      R.assoc('driversLicense'),
      R.always("Driver's License required")
    ),
    R.compose(setValidationMessages, R.assoc('driversLicense'), R.always(''))
  );

  const handleFirstNameChange = R.compose(
    setDriverState,
    R.assoc('firstName'),
    R.tap(validateFirstName),
    R.path(['target', 'value'])
  );

  const handleLastNameChange = R.compose(
    setDriverState,
    R.assoc('lastName'),
    R.tap(validateLastName),
    R.path(['target', 'value'])
  );

  const handleDriversLicenseChange = R.compose(
    setDriverState,
    R.assoc('driversLicense'),
    R.tap(validateDriversLicense),
    R.path(['target', 'value'])
  );

  const handleDriversLicenseStateChange = R.compose(
    setDriverState,
    R.assoc('driversLicenseState'),
    R.path(['target', 'value'])
  );

  const buttons = (
    <Box sx={{ display: 'flex', justifyContent: 'space-around', width: 1 }}>
      <Button disabled={isInvalid} onClick={next} sx={{ width: 150 }}>
        Continue
      </Button>
    </Box>
  );

  return (
    <DrawerLayoutWrapper
      buttons={buttons}
      headerButtonText="CLOSE"
      isLoading={isLoadingStates}
      onHeaderButtonClick={onClose}
      title="New Driver"
    >
      <ProgressBar
        label="Driver (1 of 4)"
        sx={{
          '&.MuiLinearProgress-colorPrimary > .MuiLinearProgress-bar': {
            borderRadius: '10px',
          },
        }}
        value={25}
      />
      <Stack spacing={2} sx={{ p: 3 }}>
        <Typography variant="body1">Driver Information</Typography>

        <TextField
          error={notEmpty(validationMessages.firstName)}
          fullWidth
          helperText={validationMessages.firstName}
          label="First Name"
          onChange={handleFirstNameChange}
          value={firstName}
        />

        <TextField
          error={notEmpty(validationMessages.lastName)}
          fullWidth
          helperText={validationMessages.lastName}
          label="Last Name"
          onChange={handleLastNameChange}
          value={lastName}
        />

        <TextField
          error={notEmpty(validationMessages.driversLicense)}
          fullWidth
          helperText={validationMessages.driversLicense}
          label="Driver's License #"
          onChange={handleDriversLicenseChange}
          value={driversLicense}
        />

        <FormControl fullWidth>
          <InputLabel id="state-label">Driver&apos;s License State</InputLabel>
          <Select
            label="Driver's License State"
            labelId="state-label"
            onChange={handleDriversLicenseStateChange}
            value={driversLicenseState}
          >
            {states.map(state => (
              <MenuItem key={state.stateId} value={state}>
                {state.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>
    </DrawerLayoutWrapper>
  );
};

DriverInformation.propTypes = {
  driverState: PropTypes.shape({
    driversLicense: PropTypes.string,
    driversLicenseState: PropTypes.oneOfType([PropTypes.string, PropTypes.shape({})]),
    firstName: PropTypes.string,
    lastName: PropTypes.string,
  }).isRequired,
  next: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  setDriverState: PropTypes.func.isRequired,
};

export default DriverInformation;
